import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { B2B_OPTIONS, SERVICE_NEEDED_OPTIONS, CONTACT } from "@/lib/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  organization: "",
  contact_person: "",
  email: "",
  phone: "",
  service_needed: "",
  message: "",
};

const validate = (v) => {
  const errs = {};
  if (!v.organization || v.organization.trim().length < 2)
    errs.organization = "Please enter your organization name.";
  if (!v.contact_person || v.contact_person.trim().length < 2)
    errs.contact_person = "Please enter a contact person.";
  if (!v.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email))
    errs.email = "Please enter a valid email.";
  if (!v.service_needed) errs.service_needed = "Please select a service.";
  if (!v.message || v.message.trim().length < 10)
    errs.message = "Please provide a brief message (min 10 characters).";
  return errs;
};

export const B2BPartnerships = () => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState(null);

  const onChange = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setValues((v) => ({ ...v, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus("submitting");
    setServerError(null);
    try {
      await axios.post(`${API}/partnerships`, values);
      setStatus("success");
      setValues(initial);
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setServerError(
        typeof detail === "string" ? detail : "Something went wrong. Please try again.",
      );
    }
  };

  return (
    <section
      data-testid="b2b-section"
      className="relative bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              B2B & Corporate Partnerships
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A2540]">
              For clinics, corporates,
              <br />
              <span className="italic text-[#2E5AA5]">and collection centres.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="font-sans text-base text-[#475569] leading-relaxed">
              Partner with a Gold Medalist consultant pathologist. From
              outsourcing complex histopathology to annual corporate wellness
              programmes — designed for local clinics, small nursing homes,
              corporate HR teams, and standalone diagnostic labs.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#E2E8F0] mb-20"
          data-testid="b2b-options"
        >
          {B2B_OPTIONS.map((o, i) => (
            <motion.div
              key={o.title}
              data-testid={`b2b-option-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.6,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="p-8 lg:p-10 border-r border-b border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] transition-colors"
            >
              <Building2
                size={22}
                strokeWidth={1.3}
                className="text-[#B08A4A] mb-6"
              />
              <h3 className="font-serif text-2xl text-[#0A2540] mb-3">
                {o.title}
              </h3>
              <p className="font-sans text-sm text-[#475569] leading-relaxed">
                {o.body}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              Partner With Us
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#0A2540] mb-6">
              Start a conversation.
            </h2>
            <p className="font-sans text-base text-[#475569] leading-relaxed mb-8">
              Tell us a little about your organization and what you need. We
              typically respond within one working day with a discovery call
              proposal.
            </p>
            <div className="space-y-3 font-sans text-sm text-[#0A2540] border-t border-[#E2E8F0] pt-6">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mr-2">
                  Direct
                </span>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="hover:text-[#2E5AA5]"
                  data-testid="b2b-direct-email"
                >
                  {CONTACT.email}
                </a>
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mr-2">
                  Reply
                </span>
                Within one working day
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {status === "success" ? (
              <div
                data-testid="b2b-form-success"
                className="border border-[#0A2540] bg-[#0A2540] text-white p-10 lg:p-14"
              >
                <CheckCircle2
                  size={40}
                  strokeWidth={1.2}
                  className="text-[#B08A4A] mb-6"
                />
                <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3">
                  Inquiry received
                </div>
                <h3 className="font-serif text-3xl lg:text-4xl mb-4 leading-tight">
                  Thank you — we&rsquo;ve got your details.
                </h3>
                <p className="font-sans text-base text-white/75 leading-relaxed max-w-lg">
                  A member of our team will reach out within one working day
                  to schedule a short discovery call. For urgent matters, you
                  may also email us directly at{" "}
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="underline decoration-[#B08A4A] underline-offset-4"
                  >
                    {CONTACT.email}
                  </a>
                  .
                </p>
                <button
                  data-testid="b2b-form-reset"
                  onClick={() => setStatus("idle")}
                  className="mt-8 inline-flex items-center gap-3 bg-transparent text-white border border-white/40 hover:border-white hover:bg-white/10 px-6 py-3 text-[10px] tracking-widest-plus uppercase transition-all"
                >
                  Submit another inquiry
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <form
                data-testid="b2b-form"
                onSubmit={onSubmit}
                noValidate
                className="border border-[#E2E8F0] p-8 lg:p-10 bg-white"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="label-clean" htmlFor="organization">
                      Organization Name *
                    </label>
                    <input
                      id="organization"
                      type="text"
                      data-testid="b2b-input-organization"
                      value={values.organization}
                      onChange={onChange("organization")}
                      className={`input-clean ${errors.organization ? "input-error" : ""}`}
                      placeholder="e.g. Sunrise Diagnostics Pvt. Ltd."
                    />
                    {errors.organization && (
                      <p
                        data-testid="b2b-error-organization"
                        className="mt-1 font-sans text-xs text-red-600"
                      >
                        {errors.organization}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label-clean" htmlFor="contact_person">
                      Contact Person *
                    </label>
                    <input
                      id="contact_person"
                      type="text"
                      data-testid="b2b-input-contact-person"
                      value={values.contact_person}
                      onChange={onChange("contact_person")}
                      className={`input-clean ${errors.contact_person ? "input-error" : ""}`}
                      placeholder="Full name"
                    />
                    {errors.contact_person && (
                      <p
                        data-testid="b2b-error-contact-person"
                        className="mt-1 font-sans text-xs text-red-600"
                      >
                        {errors.contact_person}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="label-clean" htmlFor="email">
                      Work Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      data-testid="b2b-input-email"
                      value={values.email}
                      onChange={onChange("email")}
                      className={`input-clean ${errors.email ? "input-error" : ""}`}
                      placeholder="name@organization.com"
                    />
                    {errors.email && (
                      <p
                        data-testid="b2b-error-email"
                        className="mt-1 font-sans text-xs text-red-600"
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label-clean" htmlFor="phone">
                      Phone (optional)
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      data-testid="b2b-input-phone"
                      value={values.phone}
                      onChange={onChange("phone")}
                      className="input-clean"
                      placeholder="+91 __________"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="label-clean" htmlFor="service_needed">
                    Service Needed *
                  </label>
                  <Select
                    value={values.service_needed}
                    onValueChange={onChange("service_needed")}
                  >
                    <SelectTrigger
                      id="service_needed"
                      data-testid="b2b-select-service"
                      className={`rounded-none border-[#E2E8F0] focus:border-[#0A2540] focus:ring-1 focus:ring-[#0A2540]/20 h-12 font-sans text-sm ${
                        errors.service_needed ? "input-error" : ""
                      }`}
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {SERVICE_NEEDED_OPTIONS.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`b2b-select-option-${s.replace(/\s+/g, "-").toLowerCase()}`}
                          className="rounded-none font-sans text-sm"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.service_needed && (
                    <p
                      data-testid="b2b-error-service"
                      className="mt-1 font-sans text-xs text-red-600"
                    >
                      {errors.service_needed}
                    </p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="label-clean" htmlFor="message">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    data-testid="b2b-input-message"
                    value={values.message}
                    onChange={onChange("message")}
                    className={`input-clean resize-y ${errors.message ? "input-error" : ""}`}
                    placeholder="Tell us about your organization, volume, and what you need help with…"
                  />
                  {errors.message && (
                    <p
                      data-testid="b2b-error-message"
                      className="mt-1 font-sans text-xs text-red-600"
                    >
                      {errors.message}
                    </p>
                  )}
                </div>

                {serverError && (
                  <div
                    data-testid="b2b-form-error"
                    className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 font-sans text-sm text-red-700"
                  >
                    <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5" />
                    <span>{serverError}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#E2E8F0] pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
                    Fields marked * are required.
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="b2b-form-submit"
                    className="group btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 size={14} strokeWidth={1.8} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <ArrowUpRight
                          size={14}
                          strokeWidth={1.5}
                          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BPartnerships;
